import React, { useState, useEffect } from 'react';
import { X, Users, Sparkles, TrendingUp, Package } from 'lucide-react';
import marketingService from '../services/marketingService';

/**
 * Модалка с информацией о клонах пользователя
 * Показывает количество клонов по каждому тарифу и уровню
 */
const ClonesInfoModal = ({ isOpen, onClose }) => {
    const [loading, setLoading] = useState(true);
    const [clonesData, setClonesData] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (isOpen) {
            loadClonesData();
        }
    }, [isOpen]);

    const loadClonesData = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await marketingService.getAllClones();

            if (response.success) {
                setClonesData(response.data);
            } else {
                setError(response.message || 'Ошибка загрузки данных');
            }
        } catch (err) {
            console.error('Error loading clones data:', err);
            setError('Не удалось загрузить информацию о клонах');
        } finally {
            setLoading(false);
        }
    };

    const getTariffName = (tarif) => {
        const tariffs = {
            'start': 'СТАРТ',
            'business': 'БИЗНЕС',
            'profit': 'ПРОФИТ',
            'turbo': 'ТУРБО',
            'vip': 'VIP'
        };
        return tariffs[tarif] || tarif.toUpperCase();
    };

    const getTariffColor = (tarif) => {
        const colors = {
            'start': 'from-blue-500 to-blue-600',
            'business': 'from-green-500 to-green-600',
            'profit': 'from-purple-500 to-purple-600',
            'turbo': 'from-orange-500 to-orange-600',
            'vip': 'from-red-500 to-red-600'
        };
        return colors[tarif] || 'from-gray-500 to-gray-600';
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden shadow-2xl">
                {/* Заголовок */}
                <div className="bg-gradient-to-r from-teal-500 to-blue-500 p-6 text-white relative">
                    <button
                        onClick={onClose}
                        className="absolute top-4 right-4 p-2 hover:bg-white/20 rounded-full transition"
                    >
                        <X size={24} />
                    </button>

                    <div className="flex items-center gap-3">
                        <div className="p-3 bg-white/20 rounded-xl backdrop-blur-sm">
                            <Package size={28} />
                        </div>
                        <div>
                            <h2 className="text-2xl font-bold">Мои клоны</h2>
                            <p className="text-white/90 text-sm mt-1">
                                Информация о ваших клонах по тарифам и уровням
                            </p>
                        </div>
                    </div>
                </div>

                {/* Контент */}
                <div className="p-6 overflow-y-auto max-h-[calc(90vh-180px)]">
                    {loading ? (
                        <div className="flex flex-col items-center justify-center py-12">
                            <div className="animate-spin rounded-full h-12 w-12 border-4 border-teal-500 border-t-transparent mb-4"></div>
                            <p className="text-gray-600">Загрузка данных...</p>
                        </div>
                    ) : error ? (
                        <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
                            <p className="text-red-600 font-medium">{error}</p>
                            <button
                                onClick={loadClonesData}
                                className="mt-4 px-6 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition"
                            >
                                Попробовать снова
                            </button>
                        </div>
                    ) : clonesData && clonesData.all_clones && clonesData.all_clones.length > 0 ? (
                        <>
                            {/* Общая статистика */}
                            <div className="bg-gradient-to-br from-teal-50 to-blue-50 rounded-xl p-6 mb-6">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                        <div className="p-3 bg-teal-500 text-white rounded-xl">
                                            <Sparkles size={24} />
                                        </div>
                                        <div>
                                            <p className="text-sm text-gray-600">Всего клонов</p>
                                            <p className="text-3xl font-bold text-gray-800">
                                                {clonesData.total_clones}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="text-right">
                                        <p className="text-sm text-gray-600">Активных тарифов</p>
                                        <p className="text-2xl font-bold text-gray-800">
                                            {Object.keys(clonesData.clones_by_tariff || {}).length}
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* Список клонов по тарифам */}
                            <div className="space-y-4">
                                {Object.entries(clonesData.clones_by_tariff || {}).map(([tarif, levels]) => (
                                    <div key={tarif} className="border border-gray-200 rounded-xl overflow-hidden">
                                        {/* Заголовок тарифа */}
                                        <div className={`bg-gradient-to-r ${getTariffColor(tarif)} p-4 text-white`}>
                                            <div className="flex items-center justify-between">
                                                <div className="flex items-center gap-3">
                                                    <TrendingUp size={20} />
                                                    <span className="font-bold text-lg">{getTariffName(tarif)}</span>
                                                </div>
                                                <div className="bg-white/20 px-3 py-1 rounded-full text-sm font-medium">
                                                    {levels.reduce((sum, level) => sum + level.clones, 0)} клонов
                                                </div>
                                            </div>
                                        </div>

                                        {/* Уровни */}
                                        <div className="bg-white divide-y divide-gray-100">
                                            {levels.map((levelData, index) => (
                                                <div key={index} className="p-4 hover:bg-gray-50 transition">
                                                    <div className="flex items-center justify-between">
                                                        <div className="flex items-center gap-4">
                                                            <div className="flex items-center justify-center w-12 h-12 bg-gray-100 rounded-xl">
                                                                <Users size={20} className="text-gray-600" />
                                                            </div>
                                                            <div>
                                                                <p className="font-semibold text-gray-800">
                                                                    Уровень {levelData.number_level}
                                                                </p>
                                                                <p className="text-sm text-gray-500">
                                                                    {levelData.level} ₽
                                                                </p>
                                                            </div>
                                                        </div>
                                                        <div className="text-right">
                                                            <div className="inline-flex items-center gap-2 bg-teal-100 text-teal-700 px-4 py-2 rounded-lg">
                                                                <Sparkles size={16} />
                                                                <span className="font-bold text-lg">
                                                                    {levelData.clones}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {/* Подсказка */}
                            <div className="mt-6 bg-blue-50 border border-blue-200 rounded-xl p-4">
                                <p className="text-sm text-blue-800">
                                    💡 <strong>Подсказка:</strong> Эти клоны можно использовать для установки в дерево без дополнительной оплаты.
                                    При покупке позиции выберите "Обычный клон" если у вас есть доступные клоны.
                                </p>
                            </div>
                        </>
                    ) : (
                        <div className="text-center py-12">
                            <div className="inline-flex items-center justify-center w-20 h-20 bg-gray-100 rounded-full mb-4">
                                <Package size={40} className="text-gray-400" />
                            </div>
                            <h3 className="text-xl font-semibold text-gray-800 mb-2">
                                У вас пока нет клонов
                            </h3>
                            <p className="text-gray-600 max-w-md mx-auto">
                                Клоны появятся после того, как участники, которых вы пригласили,
                                начнут зарабатывать в системе.
                            </p>
                        </div>
                    )}
                </div>

                {/* Футер */}
                <div className="border-t border-gray-200 p-4 bg-gray-50">
                    <button
                        onClick={onClose}
                        className="w-full py-3 bg-gradient-to-r from-teal-500 to-blue-500 text-white rounded-xl font-semibold hover:from-teal-600 hover:to-blue-600 transition"
                    >
                        Закрыть
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ClonesInfoModal;
